import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import swal from 'sweetalert';
import Api from '@/Api';
import { isAuthenticated } from '@/Auth';
import { getApiErrorCode, getApiErrorMessage } from '@/helpers/api-error-message';
import { describeReportExport, isReportExportActive } from '@/helpers/report-export-view';
import { REPORT_EXPORT_LEAVE_MS, ReportExportsPanel, type ReportExportsPanelView } from '@/components/ReportExportsPanel';
import type { CreateReportExportBody } from '../../shared/schemas/report-export/create-report-export.schema';
import type { ReportExportJob } from '../../shared/schemas/report-export/report-export.schema';

/**
 * Exportações assíncronas de relatórios (MF-1143). Guarda as tarefas do usuário, faz polling enquanto alguma
 * está em andamento e renderiza o painel do canto inferior direito, que sobrevive à navegação. O estado vem
 * sempre do backend: depois de um F5 o painel é remontado a partir da lista persistida.
 *
 * Polling e não WebSocket: o worker roda em outro processo, sem socket.io, e não existe sala por usuário
 * para empurrar o aviso (ver docs/features/report-exports.md § Atualização do painel).
 */

interface ReportExportsContextType {
	startExport: (input: CreateReportExportBody) => Promise<void>;
}

/** Exportado para class components (`static contextType`); componentes de função usam `useReportExports`. */
export const ReportExportsContext = createContext<ReportExportsContextType | undefined>(undefined);

export const useReportExports = () => {
	const context = useContext(ReportExportsContext);
	if (!context) {
		throw new Error('useReportExports deve ser usado dentro de um ReportExportsProvider');
	}
	return context;
};

const POLL_VISIBLE_MS = 3000;
const POLL_HIDDEN_MS = 15000;
/** Exportações terminadas aparecem no painel por este tempo; depois só ficam no histórico do backend. */
const FINISHED_VISIBLE_MS = 24 * 60 * 60 * 1000;

const VIEW_KEY = 'report-exports-view';
const VIEWS: ReportExportsPanelView[] = ['expanded', 'compact', 'hidden'];

// Preferências do próprio navegador: se falharem (modo privado, storage bloqueado), o painel só não lembra.
const readStorage = <T,>(key: string, fallback: T): T => {
	try {
		const raw = window.localStorage.getItem(key);
		return raw === null ? fallback : (JSON.parse(raw) as T);
	} catch {
		return fallback;
	}
};

const writeStorage = (key: string, value: unknown): void => {
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// sem storage, a preferência vale só nesta aba
	}
};

const isRecentlyFinished = (job: ReportExportJob, now: number): boolean => now - new Date(job.finished_at ?? job.created_at).getTime() < FINISHED_VISIBLE_MS;

const readView = (): ReportExportsPanelView => {
	const stored = readStorage<string>(VIEW_KEY, 'compact');
	return VIEWS.find(view => view === stored) ?? 'compact';
};

/**
 * Abre a URL temporária numa aba nova, como o PdfButton: o S3 responde com `attachment` e o navegador só baixa;
 * se a URL falhar, o erro aparece na aba nova e o usuário não perde a tela do Follow.
 */
const openDownload = (url: string, fileName: string | null) => {
	const link = document.createElement('a');
	link.href = url;
	link.target = '_blank';
	link.rel = 'noopener noreferrer';
	if (fileName) link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const ReportExportsProvider = ({ children }: { children: React.ReactNode }) => {
	const [jobs, setJobs] = useState<ReportExportJob[]>([]);
	// Sem preferência gravada (outro navegador, aba privada, storage limpo) o painel começa minimizado: o mesmo
	// usuário pode ter exportações de outra sessão e o painel aberto cobriria a tela no login. No mesmo navegador
	// (F5, nova aba) vale o que a pessoa escolheu; iniciar uma exportação expande o painel.
	const [view, setView] = useState<ReportExportsPanelView>(readView);
	const [busyIds, setBusyIds] = useState<number[]>([]);
	// Itens saindo da lista (animação) e itens já baixados nesta sessão, que perguntam se podem ser removidos.
	const [leavingIds, setLeavingIds] = useState<number[]>([]);
	const [downloadedIds, setDownloadedIds] = useState<number[]>([]);
	const [isPageVisible, setIsPageVisible] = useState(() => document.visibilityState !== 'hidden');
	const previousStatuses = useRef(new Map<number, ReportExportJob['status']>());
	// Sobe a cada mudança local (criar, cancelar, gerar de novo). Uma lista pedida antes dela chega velha e não
	// pode substituir a atual: apagaria do painel a exportação que acabou de ser criada.
	const localChangeVersion = useRef(0);

	/** Avisa por toast quando uma exportação acompanhada termina, mesmo com o painel minimizado. */
	const notifyTransitions = useCallback((next: ReportExportJob[]) => {
		next.forEach(job => {
			const before = previousStatuses.current.get(job.id);
			if (before && before !== job.status && isReportExportActive({ status: before })) {
				const { title } = describeReportExport(job);
				if (job.status === 'completed') toast.success(`${title}: arquivo pronto para baixar.`);
				else if (job.status === 'failed') toast.warn(`${title}: não foi possível gerar o arquivo.`);
			}
			previousStatuses.current.set(job.id, job.status);
		});
	}, []);

	const refresh = useCallback(async () => {
		const versionAtRequest = localChangeVersion.current;
		try {
			const res = await Api.listReportExports();
			if (versionAtRequest !== localChangeVersion.current) return;
			notifyTransitions(res.data);
			setJobs(res.data);
		} catch {
			// falha de rede: mantém a última lista e tenta de novo no próximo ciclo
		}
	}, [notifyTransitions]);

	useEffect(() => {
		if (!isAuthenticated()) return;
		void refresh();
	}, [refresh]);

	useEffect(() => {
		const onVisibilityChange = () => {
			const visible = document.visibilityState !== 'hidden';
			setIsPageVisible(visible);
			if (visible && isAuthenticated()) void refresh();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => document.removeEventListener('visibilitychange', onVisibilityChange);
	}, [refresh]);

	const hasActive = jobs.some(isReportExportActive);

	// Polling só enquanto há exportação em andamento; mais espaçado com a aba em segundo plano.
	useEffect(() => {
		if (!hasActive) return;
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout>;

		const schedule = () => {
			timer = setTimeout(
				async () => {
					await refresh();
					if (!cancelled) schedule();
				},
				isPageVisible ? POLL_VISIBLE_MS : POLL_HIDDEN_MS
			);
		};
		schedule();

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [hasActive, isPageVisible, refresh]);

	const changeView = useCallback((next: ReportExportsPanelView) => {
		setView(next);
		writeStorage(VIEW_KEY, next);
	}, []);

	const upsert = useCallback((job: ReportExportJob) => {
		localChangeVersion.current += 1;
		previousStatuses.current.set(job.id, job.status);
		setJobs(current => [job, ...current.filter(item => item.id !== job.id)]);
	}, []);

	const removeLocally = useCallback((id: number) => {
		localChangeVersion.current += 1;
		setJobs(current => current.filter(item => item.id !== id));
		setLeavingIds(current => current.filter(item => item !== id));
		setDownloadedIds(current => current.filter(item => item !== id));
	}, []);

	const withBusy = useCallback(async (id: number, action: () => Promise<void>) => {
		setBusyIds(current => [...current, id]);
		try {
			await action();
		} finally {
			setBusyIds(current => current.filter(item => item !== id));
		}
	}, []);

	const startExport = useCallback(
		async (input: CreateReportExportBody) => {
			try {
				const res = await Api.createReportExport(input);
				upsert(res.data);
				changeView('expanded');
			} catch (err) {
				const code = getApiErrorCode(err);
				const title = code === 'EXPORT_PENDING_LIMIT' ? 'Limite de exportações em andamento' : 'Não foi possível iniciar a exportação';
				swal(title, getApiErrorMessage(err, 'Tente novamente em instantes.'), 'warning');
			}
		},
		[changeView, upsert]
	);

	const cancel = useCallback(
		(job: ReportExportJob) =>
			withBusy(job.id, async () => {
				try {
					const res = await Api.cancelReportExport(job.id);
					upsert(res.data);
				} catch (err) {
					swal('Não foi possível cancelar a exportação', getApiErrorMessage(err, 'Tente novamente em instantes.'), 'warning');
					await refresh();
				}
			}),
		[refresh, upsert, withBusy]
	);

	const retry = useCallback(
		(job: ReportExportJob) =>
			withBusy(job.id, async () => {
				try {
					const res = await Api.retryReportExport(job.id);
					upsert(res.data);
					// A tarefa nova substitui a anterior no painel; sem arquivo (ou com o expirado), removê-la não perde nada.
					removeLocally(job.id);
					Api.dismissReportExport(job.id).catch(() => undefined);
				} catch (err) {
					const code = getApiErrorCode(err);
					const title = code === 'EXPORT_PENDING_LIMIT' ? 'Limite de exportações em andamento' : 'Não foi possível gerar novamente';
					swal(title, getApiErrorMessage(err, 'Tente novamente em instantes.'), 'warning');
				}
			}),
		[removeLocally, upsert, withBusy]
	);

	const download = useCallback(
		(job: ReportExportJob) =>
			withBusy(job.id, async () => {
				try {
					const res = await Api.downloadReportExport(job.id);
					openDownload(res.data.url, job.file_name);
					setDownloadedIds(current => (current.includes(job.id) ? current : [...current, job.id]));
				} catch (err) {
					if (getApiErrorCode(err) === 'EXPORT_EXPIRED') await refresh();
					swal('Não foi possível baixar o arquivo', getApiErrorMessage(err, 'Tente novamente em instantes.'), 'warning');
				}
			}),
		[refresh, withBusy]
	);

	/** "Remover da lista": vale em todos os navegadores e apaga o arquivo do S3; o item sai com uma animação curta. */
	const dismiss = useCallback(
		(job: ReportExportJob) =>
			withBusy(job.id, async () => {
				try {
					await Api.dismissReportExport(job.id);
					setLeavingIds(current => [...current, job.id]);
					setTimeout(() => removeLocally(job.id), REPORT_EXPORT_LEAVE_MS);
				} catch (err) {
					// Já removida em outro navegador: só some daqui também.
					if (getApiErrorCode(err) === 'NOT_FOUND') {
						removeLocally(job.id);
						return;
					}
					swal('Não foi possível remover da lista', getApiErrorMessage(err, 'Tente novamente em instantes.'), 'warning');
					await refresh();
				}
			}),
		[refresh, removeLocally, withBusy]
	);

	const keep = useCallback((job: ReportExportJob) => setDownloadedIds(current => current.filter(item => item !== job.id)), []);

	// Expiradas não aparecem: o arquivo não existe mais para baixar.
	const visibleJobs = useMemo(() => {
		const now = Date.now();
		return jobs.filter(job => isReportExportActive(job) || (job.status !== 'expired' && isRecentlyFinished(job, now)));
	}, [jobs]);

	const value = useMemo(() => ({ startExport }), [startExport]);

	return (
		<ReportExportsContext.Provider value={value}>
			{children}
			{visibleJobs.length > 0 && (
				<ReportExportsPanel
					jobs={visibleJobs}
					view={view}
					busyIds={busyIds}
					leavingIds={leavingIds}
					downloadedIds={downloadedIds}
					onChangeView={changeView}
					onCancel={cancel}
					onRetry={retry}
					onDownload={download}
					onDismiss={dismiss}
					onKeep={keep}
				/>
			)}
		</ReportExportsContext.Provider>
	);
};
